/**
 * LoanFactsStrip — the loan's headline facts, hoisted to the top of the closing
 * panel so a closer never has to open the loan to answer "which loan is this?".
 *
 * Four facts by default (status · product · purpose · total amount); a gear
 * opens a checklist of every fact the panel can show, and the pick is
 * remembered per partner build (partnerStorage). The catalog reads from the
 * schedule CARD / readiness object (`view`) first and the loan itself (`full`)
 * for anything the card doesn't carry — a fact whose source hasn't loaded
 * renders "—" rather than disappearing, so the strip never jumps.
 */
import { useCallback, useMemo, useState } from "react";

import CheckRounded from "@mui/icons-material/CheckRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import TuneRounded from "@mui/icons-material/TuneRounded";
import { Box, Checkbox, IconButton, Popover, Tooltip } from "@mui/material";

import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import { formatDate } from "../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../_src_shared/utils/functions/formatDateTime";
import { formatPercent } from "../../utils/numbers/formatPercent";
import { partnerStorage } from "../../utils/storage/partnerStorage";
import { slate, teal } from "../../theme/colors";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import { useUserName } from "./ClosingPanelSections";
import { C, isTrade, pipelineStatusLabel } from "./closingReadinessUtils";
import { TYPE } from "./typography";

const STORAGE_KEY = "closingDetailDrawer.facts";
const EMPTY = "—";

const dollar = (v) => (v == null || v === "" ? EMPTY : formatDollar(v, EMPTY));
const pct = (v) => (v == null || v === "" ? EMPTY : formatPercent(Number(v)));
const day = (v) => (v ? formatDate(v, EMPTY) : EMPTY);
const clock = (v) => (v ? formatDateTime(v, "datetime") : EMPTY);
const text = (v) => (v == null || v === "" ? EMPTY : String(v));
const months = (v) => (v == null || v === "" ? EMPTY : `${v} mo`);

// The catalog. `get(view, full, h)` returns the DISPLAY string; `trade` marks
// facts that only make sense on an acquisition (hidden on a closing), `preclose`
// the reverse. `h.userName` resolves an email to a display name.
export const FACT_CATALOG = [
  { group: "Loan", key: "loanStatus", label: "Status", get: (v) => text(pipelineStatusLabel(v)) },
  { group: "Loan", key: "tradeStatus", label: "Trade status", trade: true, get: (v) => text(v?.tradeStatus) },
  { group: "Loan", key: "loanProductType", label: "Product", get: (v) => text(v?.loanProductType) },
  { group: "Loan", key: "loanPurpose", label: "Purpose", get: (v, f) => text(f?.loanPurpose) },
  { group: "Loan", key: "totalLoanAmount", label: "Total loan amount", get: (v, f) => dollar(f?.totalLoanAmount ?? v?.loanAmount) },
  { group: "Loan", key: "baseLoanAmount", label: "Base loan amount", get: (v, f) => dollar(f?.baseLoanAmount) },
  { group: "Loan", key: "totalHoldback", label: "Total holdback", get: (v, f) => dollar(f?.totalHoldback) },
  { group: "Loan", key: "interestRate", label: "Interest rate", get: (v, f) => pct(f?.interestRate) },
  { group: "Loan", key: "termMonths", label: "Term", get: (v, f) => months(f?.termMonths) },
  { group: "Loan", key: "amortizationType", label: "Amortization", get: (v, f) => text(f?.amortizationType) },
  { group: "Loan", key: "fundingType", label: "Funding type", preclose: true, get: (v, f) => text(f?.fundingType) },
  { group: "Loan", key: "originator", label: "Originator", get: (v) => text(v?.originator) },
  { group: "Loan", key: "purchasePriceAmount", label: "Purchase price", trade: true, get: (v, f) => dollar(f?.purchasePriceAmount) },

  { group: "Dates", key: "expectedClosingDate", label: "Expected closing", get: (v, f) => day(f ? f.expectedClosingDate : v?.expectedDate ?? v?.scheduling?.expectedDate) },
  { group: "Dates", key: "expectedSettlementDate", label: "Expected settlement", trade: true, get: (v, f) => day(f?.expectedSettlementDate) },
  { group: "Dates", key: "closingScheduledDate", label: "Scheduled closing", preclose: true, get: (v) => clock(v?.scheduledDate ?? v?.scheduling?.scheduledDate) },
  { group: "Dates", key: "settlementDate", label: "Settlement date", trade: true, get: (v) => day(v?.scheduledDate ?? v?.scheduling?.scheduledDate) },
  { group: "Dates", key: "closingCutoffDate", label: "Closing cutoff", get: (v, f) => day(f?.closingCutoffDate ?? v?.scheduling?.cutoffDate) },
  { group: "Dates", key: "fundedDate", label: "Funded date", get: (v, f) => day(f?.fundedDate ?? v?.closedDate) },

  { group: "Team", key: "closer", label: "Closer", get: (v, f, h) => text(h.userName(v?.closer)) },
  { group: "Team", key: "underwriter", label: "Underwriter", get: (v, f, h) => text(h.userName(v?.underwriter ?? f?.underwriter)) },
  { group: "Team", key: "processor", label: "Processor", get: (v, f, h) => text(h.userName(v?.processor ?? f?.processor)) },
  { group: "Team", key: "salesperson", label: "Salesperson", get: (v, f, h) => text(h.userName(f?.salesperson)) },

  { group: "Parties", key: "borrowerNames", label: "Borrower(s)", get: (v) => text((v?.borrowerNames || []).filter(Boolean).join(", ")) },
  { group: "Parties", key: "entityNames", label: "Entity", get: (v) => text((v?.entityNames || []).filter(Boolean).join(", ")) },
  { group: "Parties", key: "propertyCount", label: "Properties", get: (v, f) => text(f?.subjectProperties?.length ?? v?.propertyAddresses?.length) },
];

export const DEFAULT_FACT_KEYS = ["loanStatus", "loanProductType", "loanPurpose", "totalLoanAmount"];

const readStored = () => {
  try {
    const raw = partnerStorage.getItem(STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : null;
    if (Array.isArray(arr) && arr.every((k) => typeof k === "string")) return arr;
  } catch (e) {
    /* storage unavailable / corrupt → defaults */
  }
  return null;
};

// The chosen keys, in CATALOG order (so the tiles never reorder as picks change).
export const useLoanFactKeys = () => {
  const [keys, setKeys] = useState(() => readStored() || DEFAULT_FACT_KEYS);
  const set = useCallback((next) => {
    const ordered = FACT_CATALOG.map((f) => f.key).filter((k) => next.includes(k));
    setKeys(ordered);
    try {
      partnerStorage.setItem(STORAGE_KEY, JSON.stringify(ordered));
    } catch (e) {
      /* storage unavailable */
    }
  }, []);
  const toggle = useCallback(
    (key) => set(keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]),
    [keys, set],
  );
  const reset = useCallback(() => set(DEFAULT_FACT_KEYS), [set]);
  return { keys, toggle, reset };
};

// One fact tile: caps label over the value. The value is the loudest text on
// the tile — that's the point of the strip.
const FactTile = ({ label, value }) => (
  <Flx column g={0.5} sx={{ minWidth: 0 }}>
    <Txt sx={{ ...TYPE.micro, fontSize: 9, letterSpacing: "0.07em", textTransform: "uppercase", color: C.faint, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {label}
    </Txt>
    <Txt
      sx={{
        ...TYPE.bodyStrong,
        fontSize: 12.5,
        lineHeight: 1.3,
        color: value === EMPTY ? C.faint : C.ink,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
      title={value}
    >
      {value}
    </Txt>
  </Flx>
);

// The gear's checklist, grouped like the catalog. Trade-only / preclose-only
// facts are hidden for the other pipeline rather than listed disabled.
const FactPicker = ({ anchorEl, onClose, keys, onToggle, onReset, trade }) => {
  const groups = useMemo(() => {
    const out = [];
    FACT_CATALOG.forEach((f) => {
      if ((f.trade && !trade) || (f.preclose && trade)) return;
      let g = out.find((x) => x.title === f.group);
      if (!g) out.push((g = { title: f.group, items: [] }));
      g.items.push(f);
    });
    return out;
  }, [trade]);

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.75,
            width: 300,
            maxHeight: "min(70vh, 560px)",
            overflowY: "auto",
            borderRadius: "12px",
            border: `1px solid ${C.line}`,
            boxShadow: "0 12px 40px -10px rgba(15,23,42,.25)",
            p: 1.5,
          },
        },
      }}
    >
      <Flx ac jb sx={{ mb: 1, px: 0.5 }}>
        <Txt sx={{ ...TYPE.sectionH, fontSize: 10.5 }}>Facts to show</Txt>
        <Tooltip title="Back to the default four" arrow>
          <IconButton size="small" onClick={onReset} sx={{ color: C.faint }}>
            <RestartAltRounded sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Flx>
      {groups.map((g) => (
        <Box key={g.title} sx={{ "& + &": { mt: 1, pt: 1, borderTop: `1px solid ${C.lineSoft}` } }}>
          <Txt sx={{ ...TYPE.micro, letterSpacing: "0.07em", textTransform: "uppercase", color: C.faint, display: "block", px: 0.5, mb: 0.25 }}>
            {g.title}
          </Txt>
          {g.items.map((f) => {
            const on = keys.includes(f.key);
            return (
              <Flx
                key={f.key}
                ac
                g={0.5}
                onClick={() => onToggle(f.key)}
                sx={{
                  px: 0.5,
                  py: "3px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  userSelect: "none",
                  "&:hover": { background: C.lineSoft },
                }}
              >
                <Checkbox
                  size="small"
                  checked={on}
                  tabIndex={-1}
                  disableRipple
                  checkedIcon={<CheckRounded sx={{ fontSize: 16 }} />}
                  sx={{ p: 0.4, color: slate[300], "&.Mui-checked": { color: teal[700] } }}
                />
                <Txt sx={{ ...TYPE.row, color: on ? C.ink : C.sub }}>{f.label}</Txt>
              </Flx>
            );
          })}
        </Box>
      ))}
    </Popover>
  );
};

// The gear that opens the picker. Lives wherever the host puts it (the drawer
// keeps it in the crumb row beside the pager); the host owns the keys via
// `useLoanFactKeys` and hands the same state to the strip.
export const LoanFactsGear = ({ keys, onToggle, onReset, trade, sx }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  return (
    <>
      <Tooltip title="Choose which loan facts show" arrow>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          aria-label="Choose loan facts"
          sx={{
            width: 30,
            height: 30,
            borderRadius: "8px",
            color: anchorEl ? C.ink : C.faint,
            background: anchorEl ? C.lineSoft : "transparent",
            "&:hover": { background: C.lineSoft, color: C.ink },
            ...sx,
          }}
        >
          <TuneRounded sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <FactPicker
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        keys={keys}
        onToggle={onToggle}
        onReset={onReset}
        trade={trade}
      />
    </>
  );
};

const LoanFactsStrip = ({ view, full, keys, sx }) => {
  const userName = useUserName();
  const trade = isTrade(view);

  const helpers = useMemo(() => ({ userName }), [userName]);
  const facts = useMemo(
    () =>
      FACT_CATALOG.filter((f) => keys.includes(f.key))
        .filter((f) => !((f.trade && !trade) || (f.preclose && trade)))
        .map((f) => ({ key: f.key, label: f.label, value: f.get(view, full, helpers) })),
    [keys, trade, view, full, helpers],
  );

  // Nothing picked → nothing rendered (no placeholder row); the crumb-row
  // gear is the way back in.
  if (facts.length === 0) return null;

  return (
    // Four equal columns, always — tiles wrap in rows of four so the spacing
    // is identical however many facts are picked.
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        columnGap: 1.5,
        rowGap: 1.75,
        ...sx,
      }}
    >
      {facts.map((f) => (
        <FactTile key={f.key} label={f.label} value={f.value} />
      ))}
    </Box>
  );
};

export default LoanFactsStrip;
